import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import CIcon from '@coreui/icons-react';
import {States} from "../../store/ducks/rootReducer";
import useModuloAtual from "../../services/useModuloAtual";
import {modulosVisiveis} from "../../services/acesso";
import {selecionarModulo} from "../../store/ducks/modulo/actions";
import '../../scss/styles-modulos.scss';

// Seta e marca desenhadas aqui: as do CoreUI tem traco fino demais nesse tamanho.
const Seta = () => (
    <svg className="seletor-modulo-seta" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6 8 10 12 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Marca = () => (
    <svg className="seletor-modulo-item-marca" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M3.5 8.5 6.5 11.5 12.5 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

/**
 * Seletor de modulo na faixa da marca (#484909), no desenho do ERP: botao com
 * o modulo atual que abre a lista dos modulos liberados, com uma linha de
 * descricao cada.
 *
 * So aparecem os modulos com alguma pagina liberada. Trocar nao abre tela:
 * vai para a raiz, em branco, so com o menu do modulo novo. Teclado:
 * Enter/Espaco/setas abrem, setas e Home/End andam, Enter escolhe, Esc e Tab
 * fecham.
 */
const SeletorModulo = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const acesso = useSelector((state: States) => state.login.data?.acesso);
    const atual = useModuloAtual();
    const [aberto, setAberto] = useState(false);
    const caixa = useRef<HTMLDivElement>(null);
    const botao = useRef<HTMLButtonElement>(null);
    const itens = useRef<Array<HTMLButtonElement | null>>([]);

    const modulos = modulosVisiveis(acesso);
    const moduloAtual = modulos.find(modulo => modulo.id === atual);
    const varios = modulos.length > 1;

    // Ao abrir, o foco vai para o modulo atual; fecha clicando fora.
    useEffect(() => {
        if (!aberto) {
            return;
        }
        const indice = Math.max(0, modulos.findIndex(modulo => modulo.id === atual));
        itens.current[indice]?.focus();

        const fora = (evento: MouseEvent) => {
            if (caixa.current && !caixa.current.contains(evento.target as Node)) {
                setAberto(false);
            }
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    if (!moduloAtual) {
        return null;
    }

    const fechar = (devolverFoco: boolean) => {
        setAberto(false);
        if (devolverFoco) {
            botao.current?.focus();
        }
    };

    const trocar = (id: string) => {
        fechar(true);
        if (id === atual) {
            return;
        }
        dispatch(selecionarModulo(id));
        history.push('/');
    };

    const teclaNoBotao = (evento: React.KeyboardEvent) => {
        if (varios && (evento.key === 'ArrowDown' || evento.key === 'ArrowUp')) {
            evento.preventDefault();
            setAberto(true);
        }
    };

    const teclaNaLista = (evento: React.KeyboardEvent, indice: number) => {
        const ultimo = modulos.length - 1;
        const focar = (i: number) => itens.current[i]?.focus();
        switch (evento.key) {
            case 'ArrowDown':
                evento.preventDefault();
                focar(indice === ultimo ? 0 : indice + 1);
                break;
            case 'ArrowUp':
                evento.preventDefault();
                focar(indice === 0 ? ultimo : indice - 1);
                break;
            case 'Home':
                evento.preventDefault();
                focar(0);
                break;
            case 'End':
                evento.preventDefault();
                focar(ultimo);
                break;
            case 'Escape':
                evento.preventDefault();
                fechar(true);
                break;
            case 'Tab':
                fechar(false);
                break;
        }
    };

    return (
        <div className={'seletor-modulo' + (aberto ? ' aberto' : '') + (varios ? '' : ' unico')} ref={caixa}>
            <button
                ref={botao}
                type="button"
                className="seletor-modulo-botao"
                disabled={!varios}
                title={varios ? 'Trocar de módulo' : undefined}
                aria-haspopup="menu"
                aria-expanded={aberto}
                aria-label={`Módulo atual: ${moduloAtual.label}${varios ? '. Trocar de módulo' : ''}`}
                onClick={() => setAberto(!aberto)}
                onKeyDown={teclaNoBotao}
            >
                <span className="seletor-modulo-icone">
                    {moduloAtual.icone && <CIcon name={moduloAtual.icone}/>}
                </span>
                <span className="seletor-modulo-textos">
                    <span className="seletor-modulo-legenda">Módulo</span>
                    <span className="seletor-modulo-nome">{moduloAtual.label}</span>
                </span>
                {varios && <Seta/>}
            </button>

            {aberto && (
                <div className="seletor-modulo-painel" role="menu" aria-label="Módulos">
                    <ul>
                        {modulos.map((modulo, indice) => {
                            const ativo = modulo.id === atual;
                            return (
                                <li key={modulo.id} role="none">
                                    <button
                                        ref={elemento => { itens.current[indice] = elemento; }}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={ativo}
                                        className={'seletor-modulo-item' + (ativo ? ' ativo' : '')}
                                        onClick={() => trocar(modulo.id)}
                                        onKeyDown={evento => teclaNaLista(evento, indice)}
                                    >
                                        <span className="seletor-modulo-item-icone">
                                            {modulo.icone && <CIcon name={modulo.icone}/>}
                                        </span>
                                        <span className="seletor-modulo-item-textos">
                                            <span className="seletor-modulo-item-nome">{modulo.label}</span>
                                            {modulo.descricao && (
                                                <span className="seletor-modulo-item-descricao">{modulo.descricao}</span>
                                            )}
                                        </span>
                                        {ativo && <Marca/>}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SeletorModulo;
